/**
 * ImageModelSelector — Provider-grouped image-model dropdown.
 *
 * Mirrors the text ModelSelector pattern (ListSubheader-grouped models per
 * provider) but ranges over image-generation models only — gpt-image-* and
 * Gemini image models.
 *
 * Used by AiAssetsSection (onboarding/strategy) and PostAssetPlannerDialog
 * (content-plan posts). Both surfaces need to (a) pick a model and (b)
 * derive the provider from the chosen model id, so callers usually pair
 * this component with `getProviderForImageModel(value)`.
 *
 * Pricing labels for OpenAI tiers are surfaced at the Quality control level,
 * not here — the model itself doesn't dictate cost (quality does).
 */

import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export type ImageModelProvider = 'openai' | 'google';

export interface ImageModelOption {
  value: string;
  label: string;
  provider: ImageModelProvider;
}

/**
 * Available image-generation models. Order within each provider group
 * goes recommended/default → high-fidelity → speed/efficiency. Top-of-list is
 * the recommended pick.
 */
export const IMAGE_MODEL_OPTIONS: ReadonlyArray<ImageModelOption> = [
  // OpenAI — gpt-image family. Verified via 2026-05 OpenAI docs.
  { value: 'gpt-image-2', label: 'GPT Image 2 (Recommended)', provider: 'openai' },
  { value: 'gpt-image-1.5', label: 'GPT Image 1.5', provider: 'openai' },
  { value: 'gpt-image-1', label: 'GPT Image 1', provider: 'openai' },
  { value: 'gpt-image-1-mini', label: 'GPT Image 1 Mini (Fast)', provider: 'openai' },
  // Google Gemini.
  {
    value: 'gemini-3.1-flash-image',
    label: 'Gemini 3.1 Flash Image / Nano Banana 2',
    provider: 'google',
  },
  {
    value: 'gemini-3.1-flash-lite-image',
    label: 'Gemini 3.1 Flash Lite Image (Fast)',
    provider: 'google',
  },
  {
    value: 'gemini-3-pro-image',
    label: 'Gemini 3 Pro Image (Best Quality)',
    provider: 'google',
  },
  {
    value: 'gemini-2.5-flash-image',
    label: 'Gemini 2.5 Flash Image / Nano Banana (Fast)',
    provider: 'google',
  },
];

/**
 * Default model used when no value is set. Matches the backend default
 * (CONTENT_PLAN_IMAGE_DEFAULT_MODEL) so existing experiments without a
 * model field land on the same provider.
 */
export const DEFAULT_IMAGE_MODEL = 'gemini-3.1-flash-image';

/**
 * Frontend mirror of `ai-core/src/providers/imageModelRouting.ts`.
 * Both files MUST stay in sync.
 */
const IMAGE_MODEL_ID_ALIASES: Record<string, string> = {
  'gemini-3.1-flash-image-preview': 'gemini-3.1-flash-image',
  'gemini-3.1-flash-lite-image-preview': 'gemini-3.1-flash-lite-image',
  'gemini-3-pro-image-preview': 'gemini-3-pro-image',
};

export function normalizeImageModelId(modelId: string): string {
  return IMAGE_MODEL_ID_ALIASES[modelId] ?? modelId;
}

/**
 * Derive the AI provider from an image-model id. Used to populate
 * `experiment.provider` when the user changes the model dropdown.
 *
 * Soft fallback ('google') matches AgentController's behavior on unknown
 * models so frontend and backend stay in sync.
 */
export function getProviderForImageModel(modelId: string): ImageModelProvider {
  const normalized = normalizeImageModelId(modelId);
  const option = IMAGE_MODEL_OPTIONS.find((o) => o.value === normalized);
  if (option) return option.provider;
  if (normalized.startsWith('gpt-image-') || normalized.startsWith('dall-e-')) return 'openai';
  return 'google';
}

const PROVIDER_LABEL: Record<ImageModelProvider, string> = {
  openai: 'OpenAI',
  google: 'Google Gemini',
};

const ALL_PROVIDERS: ImageModelProvider[] = ['openai', 'google'];

export interface ImageModelSelectorProps {
  value: string;
  onChange: (model: string) => void;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  onOpen?: () => void;
  /**
   * Available options for the current project. When omitted, the static
   * fallback catalog above is used.
   */
  options?: ReadonlyArray<ImageModelOption>;
  /**
   * Restrict the dropdown to specific providers. Default: all providers
   * shown. Pass `['openai']` to lock the surface to OpenAI etc.
   */
  providers?: ImageModelProvider[];
}

export const ImageModelSelector: React.FC<ImageModelSelectorProps> = ({
  value,
  onChange,
  size = 'small',
  sx,
  label = 'Image model',
  disabled = false,
  loading = false,
  fullWidth = false,
  onOpen,
  options,
  providers,
}) => {
  const visibleProviders = providers ?? ALL_PROVIDERS;
  const selectedValue = normalizeImageModelId(value);
  const availableOptions = options ?? IMAGE_MODEL_OPTIONS;
  const filteredOptions = availableOptions.filter((option) =>
    visibleProviders.includes(option.provider)
  );
  const hasSelectedValue = filteredOptions.some((option) => option.value === selectedValue);
  const selectValue = hasSelectedValue ? selectedValue : '';
  const reactId = React.useId();
  const labelId = `image-model-select-label-${reactId}`;
  const selectId = `image-model-select-${reactId}`;
  return (
    <FormControl
      size={size}
      sx={{ minWidth: 280, position: 'relative', ...(sx as any) }}
      fullWidth={fullWidth}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        value={selectValue}
        label={label}
        disabled={disabled || (!loading && filteredOptions.length === 0)}
        onOpen={onOpen}
        onChange={(e) => onChange(String(e.target.value))}
      >
        {visibleProviders.flatMap((provider) => {
          const items = filteredOptions.filter((o) => o.provider === provider);
          if (items.length === 0) return [];
          // Skip subheader when a single provider is shown — cleaner UI.
          const showSubheader = visibleProviders.length > 1;
          return [
            ...(showSubheader
              ? [
                  <ListSubheader key={`subheader-${provider}`}>
                    {PROVIDER_LABEL[provider]}
                  </ListSubheader>,
                ]
              : []),
            ...items.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            )),
          ];
        })}
        {!hasSelectedValue ? (
          <MenuItem value="" disabled>
            {filteredOptions.length === 0 ? 'No connected image models' : 'Select a connected image model'}
          </MenuItem>
        ) : null}
      </Select>
      {loading ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            pointerEvents: 'none',
            position: 'absolute',
            right: 34,
            top: 0,
          }}
        >
          <CircularProgress color="inherit" size={18} />
        </Box>
      ) : null}
    </FormControl>
  );
};
